/**
 * How a web source reads and what makes two of them the same link: a key for
 * spotting a URL already on the board, a title that is never just the domain
 * when the URL says more, and snippets as plain text instead of raw markdown.
 * Pure, so the renderers and the add paths agree.
 */

// Query params that only say where a click came from ('ref' stays: GitHub uses it for branches)
const TRACKING_PARAMS = new Set(['fbclid', 'gclid', 'dclid', 'msclkid', 'yclid', 'igshid', 'mc_cid', 'mc_eid', 'ref_src', 'ref_url', 'si', '_hsenc', '_hsmi', 'mkt_tok']);

/**
 * The same link however it was written: no scheme, lowercase host without
 * `www.`, no hash (unless it is an app route like `#/inbox`), no tracking
 * params (`utm_*` and friends), no trailing slash. Not a URL anyone should
 * open; only for comparing. A string that does not parse compares as itself,
 * trimmed.
 */
export function sourceKey(url: string): string {
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return url.trim();
  }
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  const params = [...parsed.searchParams].filter(([name]) => !name.toLowerCase().startsWith('utm_') && !TRACKING_PARAMS.has(name.toLowerCase()));
  const query = params.length ? `?${new URLSearchParams(params).toString()}` : '';
  const path = parsed.pathname.replace(/\/+$/, '');
  const port = parsed.port ? `:${parsed.port}` : '';
  const route = /^#!?\//.test(parsed.hash) ? parsed.hash : '';
  return `${host}${port}${path}${query}${route}`;
}

/** Keys of the links among these cards (ghosts left out). */
export function sourceKeysOf(items: ReadonlyArray<{ content?: unknown; isGhost?: boolean }>): Set<string> {
  const keys = new Set<string>();
  for (const item of items) {
    const url = (item.content as { url?: unknown } | null | undefined)?.url;
    if (!item.isGhost && typeof url === 'string' && url) keys.add(sourceKey(url));
  }
  return keys;
}

/**
 * The entries whose link is not in `onBoard`, each link once (the first
 * spelling wins), their indexes in `entries`, and how many were left out.
 */
export function newSourcesOnly<T>(entries: T[], urlOf: (entry: T) => string, onBoard: Set<string>): { fresh: T[]; kept: number[]; skipped: number } {
  const seen = new Set(onBoard);
  const fresh: T[] = [];
  const kept: number[] = [];
  entries.forEach((entry, i) => {
    const key = sourceKey(urlOf(entry));
    if (seen.has(key)) return;
    seen.add(key);
    fresh.push(entry);
    kept.push(i);
  });
  return { fresh, kept, skipped: entries.length - fresh.length };
}

/** "Added 3, 1 was already here", or what to say when every link was already here. */
export function alreadyHereMessage(added: number, skipped: number): string {
  if (added === 0) return skipped === 1 ? 'That link is already on this board' : `All ${skipped} links are already on this board`;
  return `Added ${added}, ${skipped} ${skipped === 1 ? 'was' : 'were'} already here`;
}

/** "producthunt.com" for a URL or a bare domain, '' when it is neither. */
export function hostOf(text: string): string {
  try {
    return new URL(/^https?:\/\//i.test(text) ? text : `https://${text}`).hostname.toLowerCase().replace(/^www\./, '');
  } catch {
    return '';
  }
}

// Path segments that name nothing: languages, index pages, ids
const EMPTY_SEGMENT = /^(index|default|home|amp|en|en-us|en-gb|www|p|a|s|post|posts|article|articles|blog|news|wiki|pages?)$/i;
const ID_SEGMENT = /^(\d+|[0-9a-f]{12,}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f-]+|[A-Za-z0-9]{22,})$/i;

/** "the-launch-checklist.html" -> "The launch checklist"; '' when nothing in the path reads as words. */
export function titleFromPath(url: string): string {
  let segments: string[];
  try {
    segments = new URL(url).pathname.split('/').filter(Boolean);
  } catch {
    return '';
  }
  for (const raw of segments.reverse()) {
    let segment = raw;
    try {
      segment = decodeURIComponent(raw);
    } catch {
      // Keep the raw segment when it is not valid percent-encoding
    }
    segment = segment
      .replace(/\.(html?|php|aspx?|jsp|cfm)$/i, '')
      // Ids stuck to a slug: "17350772-launch-guide", "launch-guide-3f2a1b9c7d1e"
      .replace(/^\d{4,}[-_]+/, '')
      .replace(/[-_]+(?=[0-9a-f]*\d)[0-9a-f]{8,}$/i, '');
    if (!segment || EMPTY_SEGMENT.test(segment) || ID_SEGMENT.test(segment)) continue;
    const words = segment.replace(/[-_+.]+/g, ' ').replace(/\s+/g, ' ').trim();
    if (words.length < 2 || !/[a-z]/i.test(words)) continue;
    return words.charAt(0).toUpperCase() + words.slice(1);
  }
  return '';
}

/**
 * What a source card calls the page: its title, unless that is only the
 * domain (or empty); then the last meaningful URL path segment made
 * readable; then the domain.
 */
export function sourceTitle(source: { url: string; title?: string | null; site?: string | null }): string {
  const host = hostOf(source.url);
  const title = (source.title ?? '').trim();
  const bare = title.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/+$/, '');
  if (title && bare !== host && bare !== (source.site ?? '').toLowerCase()) return title;
  return titleFromPath(source.url) || title || source.site || host || source.url;
}

/**
 * A snippet as plain text: headings, emphasis, code marks, images, links
 * (kept as their text), list and quote markers and table pipes stripped,
 * whitespace collapsed to single spaces.
 */
export function plainSnippet(text: string | null | undefined): string {
  if (!text) return '';
  return text
    .replace(/<[^>]+>/g, ' ')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\[[^\]]*\]/g, '$1')
    .replace(/^\s{0,3}#{1,6}\s+/gm, '')
    .replace(/^\s*>\s?/gm, '')
    .replace(/^\s*(?:[-*+•]|\d+[.)])\s+/gm, '')
    .replace(/^\s*(?:[-*_]\s*){3,}$/gm, ' ')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/(^|[\s(])([*_])(\S(?:.*?\S)?)\2(?=[\s).,;:!?]|$)/g, '$1$3')
    .replace(/~~(.+?)~~/g, '$1')
    .replace(/`{1,3}([^`]*)`{1,3}/g, '$1')
    .replace(/\s*\|\s*/g, ' ')
    .replace(/#{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}
